import { DeviceInstallationStatus } from "@domain/General/DeviceInstallationStatus/DeviceInstallationStatus";
import type { IDealerModeVehicle, IDealerModeVehicleDto } from "./IDealerModeVehicle";
import { useUser } from "@domain/Security";
import dayjs, { Dayjs } from "dayjs";
import { useDealerModeApi } from "./Api/DealerModeApi";
import type { DealerModeAppConfiguration } from "@domain/AppConfiguration/DealerModeAppConfiguration";

export class DealerModeVehicle implements IDealerModeVehicle
{

    // Origination
    appConfigurationId: string;
    businessConsumerId?: string;
    appName?: string;

    // Consumer
    firstName?: string;
    lastName?: string;
    businessName?: string;
    mobile?: string;
    email?: string;

    // Vehicle details
    vehicleId?: string;
    crmReferenceId?: string;
    vin?: string;
    nickname?: string;
    make?: string;
    model?: string;
    year?: number;
    rego?: string;
    regoState?: string;
    odometer: number;
    isActivated: boolean;
    isClaimed: boolean;
    isCarePlanEnabled: boolean;
    healthMonitorInstallationStatus?: DeviceInstallationStatus;
    theftRecoveryDeviceInstallationStatus?: DeviceInstallationStatus;
    utcDateCreated: Dayjs;

    constructor( model: IDealerModeVehicleDto )
    {
        this.appConfigurationId = model.appConfigurationId;
        this.businessConsumerId = model.businessConsumerId;
        this.appName = model.appName;
        this.firstName = model.firstName;
        this.lastName = model.lastName;
        this.businessName = model.businessName;
        this.mobile = model.mobile;
        this.email = model.email;
        this.vehicleId = model.vehicleId;
        this.crmReferenceId = model.crmReferenceId;
        this.vin = model.vin;
        this.nickname = model.nickname;
        this.make = model.make;
        this.model = model.model;
        this.year = model.year;
        this.rego = model.rego;
        this.regoState = model.regoState;
        this.odometer = model.odometer;
        this.isActivated = model.isActivated;
        this.isClaimed = model.isClaimed;
        this.isCarePlanEnabled = model.isCarePlanEnabled;
        this.healthMonitorInstallationStatus = model.healthMonitorInstallationStatus;
        this.theftRecoveryDeviceInstallationStatus = model.theftRecoveryDeviceInstallationStatus;
        this.utcDateCreated = dayjs( model.utcDateCreated );
    }

    /**
     * Display name of the vehicle
     */
    get displayName()
    {
        if( this.make == undefined && this.model == undefined ) return 'Make/model not available';
        return `${ this.make ?? '' } ${ this.model ?? '' } ${ ( this.year ?? 0 > 1950 ? this.year : null ) ?? '' }`.trim();
    }

    /**
     * Display name of the consumer
     */
    get consumerDisplayName()
    {
        if( this.firstName == undefined && this.lastName == undefined && this.businessName == undefined ) return 'Not assigned to a consumer';
        if( this.firstName == undefined && this.lastName == undefined ) return this.businessName!;
        return `${ this.firstName ?? '' } ${ this.lastName ?? '' }`.trim();
    }

    /**
     * True if the health monitor is installed
     */
    get hasHealthMonitor()
    {
        return this.healthMonitorInstallationStatus == DeviceInstallationStatus.Installed;
    }

    /**
     * Creates a model from a query
     * @param query
     */
    public static FromQuery( apps: DealerModeAppConfiguration[], query?: string )
    {
        const defaultApp = apps[0] ?? null;

        const model = {
            vin: undefined,
            rego: undefined,
            regoState: defaultApp?.state ?? undefined,
            appConfigurationId: defaultApp?.appConfigurationId ?? undefined,
            appConfigurationName: defaultApp?.name ?? undefined,
            healthMonitorInstallationStatus: DeviceInstallationStatus.Installed, // now defaults to installed
            isCarePlanEnabled: true // now defaults to true
        } as any;

        const queryIsNumbersAndLetters =  query && /\d/.test( query ) && /[^\d]/.test( query );

        // query was 17 characters exactly and contained at least one digit and one non digit
        if( query && query.length == 17 && queryIsNumbersAndLetters )
        {
            model.vin = query.toUpperCase();
        }

        // query was between 4 and 9 characters and contained at least one digit and one non digit
        else if( query && query.length > 3 && query.length <= 9 && queryIsNumbersAndLetters )
        {
            model.rego = query.toUpperCase();
        }

        return new DealerModeVehicle( model );

    }

    getAvailableApps = async () =>
    {
        const user = useUser();
        const appIds = user.roles.map( r => r.appIds ).flat();

        const dealerModeApi = useDealerModeApi();
        const response = await dealerModeApi.listAppConfigurationsAsync(appIds);
        if( !response.success )
        {
            throw new Error( 'Failed to fetch app configurations' );
        }

        return response.data;
    };

}
